import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import type { Plan } from "@/mocks/plans";
import { formatPlanPrice } from "@/hooks/usePlans";

const API_BASE = "https://wpp.debugin.com/ai";

interface PaymentModalProps {
  plan: Plan;
  onClose: () => void;
}

interface FormData {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  country_code: string;
}

interface FormErrors {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  country_code?: string;
}

interface Country {
  code: string;
  name: string;
  flag: string;
  digits: number;
}

const COUNTRIES: Country[] = [
  { code: "+20", name: "Egypt", flag: "🇪🇬", digits: 10 },
  { code: "+966", name: "Saudi Arabia", flag: "🇸🇦", digits: 9 },
  { code: "+971", name: "UAE", flag: "🇦🇪", digits: 9 },
  { code: "+965", name: "Kuwait", flag: "🇰🇼", digits: 8 },
  { code: "+974", name: "Qatar", flag: "🇶🇦", digits: 8 },
  { code: "+973", name: "Bahrain", flag: "🇧🇭", digits: 8 },
  { code: "+968", name: "Oman", flag: "🇴🇲", digits: 8 },
  { code: "+962", name: "Jordan", flag: "🇯🇴", digits: 9 },
  { code: "+961", name: "Lebanon", flag: "🇱🇧", digits: 8 },
  { code: "+970", name: "Palestine", flag: "🇵🇸", digits: 9 },
  { code: "+964", name: "Iraq", flag: "🇮🇶", digits: 10 },
  { code: "+249", name: "Sudan", flag: "🇸🇩", digits: 9 },
  { code: "+212", name: "Morocco", flag: "🇲🇦", digits: 9 },
  { code: "+213", name: "Algeria", flag: "🇩🇿", digits: 9 },
  { code: "+216", name: "Tunisia", flag: "🇹🇳", digits: 8 },
  { code: "+218", name: "Libya", flag: "🇱🇾", digits: 9 },
  { code: "+963", name: "Syria", flag: "🇸🇾", digits: 9 },
  { code: "+967", name: "Yemen", flag: "🇾🇪", digits: 9 },
  { code: "+1", name: "United States", flag: "🇺🇸", digits: 10 },
  { code: "+44", name: "United Kingdom", flag: "🇬🇧", digits: 10 },
  { code: "+33", name: "France", flag: "🇫🇷", digits: 9 },
  { code: "+49", name: "Germany", flag: "🇩🇪", digits: 11 },
  { code: "+90", name: "Turkey", flag: "🇹🇷", digits: 10 },
  { code: "+91", name: "India", flag: "🇮🇳", digits: 10 },
];

export default function PaymentModal({ plan, onClose }: PaymentModalProps) {
  const { t, i18n } = useTranslation("plans");
  const displayPrice = formatPlanPrice(plan.amount, i18n.language);
  const [form, setForm] = useState<FormData>({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    country_code: "+20",
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState("");
  const [showCountries, setShowCountries] = useState(false);

  const selectedCountry = COUNTRIES.find((c) => c.code === form.country_code) || COUNTRIES[0];

  const validate = (): boolean => {
    const e: FormErrors = {};
    if (!form.first_name.trim()) e.first_name = t("plans.payment.err.firstNameRequired");
    if (!form.last_name.trim()) e.last_name = t("plans.payment.err.lastNameRequired");
    if (!form.email.trim()) {
      e.email = t("plans.payment.err.emailRequired");
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      e.email = t("plans.payment.err.emailInvalid");
    }
    if (!form.phone.trim()) {
      e.phone = t("plans.payment.err.phoneRequired");
    } else if (!/^[0-9]+$/.test(form.phone)) {
      e.phone = t("plans.payment.err.phoneDigitsOnly");
    } else if (form.phone.length !== selectedCountry.digits) {
      e.phone = t("plans.payment.err.phoneLength", { digits: selectedCountry.digits });
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleChange = (field: keyof FormData, value: string) => {
    if (field === "phone") {
      value = value.replace(/\D/g, "").replace(/^0+/, "").slice(0, selectedCountry.digits);
    }
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleCountrySelect = (code: string) => {
    setForm((prev) => ({ ...prev, country_code: code, phone: "" }));
    if (errors.phone || errors.country_code) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next.phone;
        delete next.country_code;
        return next;
      });
    }
    setShowCountries(false);
  };

  const handleSubmit = async (ev: FormEvent) => {
    ev.preventDefault();
    if (!validate()) return;

    setLoading(true);
    setApiError("");

    try {
      const fullPhone = `${form.country_code}${form.phone}`;

      const res = await fetch(`${API_BASE}/pay/new`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          phone: fullPhone,
          plan: plan.key,
          first_name: form.first_name.trim(),
          last_name: form.last_name.trim(),
          email: form.email.trim(),
        }),
      });

      if (!res.ok) {
        throw new Error(`Server returned ${res.status}. Payment service may be unavailable right now.`);
      }

      const contentType = res.headers.get("content-type") || "";
      if (!contentType.includes("application/json")) {
        throw new Error("Received an unexpected response from the server. Please try again later.");
      }

      const json = await res.json();

      if (!json.success) {
        throw new Error(json.message || "Payment session could not be created");
      }

      const iframeUrl: string = json.data?.iframeUrl;
      if (!iframeUrl) {
        throw new Error("No payment URL received");
      }

      window.location.href = iframeUrl;
    } catch (err: unknown) {
      if (err instanceof SyntaxError && err.message.includes("JSON")) {
        setApiError("The payment service is currently unavailable. Please try again later or contact support.");
      } else {
        const message = err instanceof Error ? err.message : "Something went wrong. Please try again.";
        setApiError(message);
      }
    } finally {
      setLoading(false);
    }
  };

  const inputClass = (field: keyof FormErrors) =>
    `w-full px-4 py-3 rounded-xl border text-sm transition-colors outline-none ${
      errors[field]
        ? "border-red-400 bg-red-50 text-foreground-950 placeholder:text-red-300"
        : "border-background-200 bg-background-50 text-foreground-950 placeholder:text-foreground-400 focus:border-primary-400"
    }`;

  const digitPlaceholder = Array.from({ length: selectedCountry.digits }, (_, i) => ((i % 9) + 1).toString()).join("");

  const remainingDigits = selectedCountry.digits - form.phone.length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-foreground-950/60 backdrop-blur-sm" onClick={onClose}></div>

      <div className="relative w-full max-w-md bg-background-50 rounded-2xl shadow-lg overflow-hidden animate-in-modal">
        <div className="bg-primary-500 px-6 py-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-background-50/80 text-xs font-medium uppercase tracking-wider">
                {t("plans.payment.youSelected")}
              </p>
              <h2 className="text-background-50 font-heading text-xl mt-0.5">
                {t(plan.name)} {t("plans.payment.plan")}
              </h2>
            </div>
            <div className="text-right">
              <span className="text-background-50 font-heading text-2xl">{displayPrice}</span>
              <span className="text-background-50/70 text-xs block">{t("plans.payment.perDays", { duration: plan.duration })}</span>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {apiError && (
            <div className="flex items-start gap-2.5 p-3.5 rounded-xl bg-red-50 border border-red-200">
              <i className="ri-error-warning-line text-red-500 text-base flex-shrink-0 mt-px"></i>
              <p className="text-red-700 text-sm">{apiError}</p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-foreground-700 text-xs font-medium mb-1.5">
                {t("plans.payment.firstName")}
              </label>
              <input
                type="text"
                value={form.first_name}
                onChange={(e) => handleChange("first_name", e.target.value)}
                placeholder={t("plans.payment.firstNamePlaceholder")}
                className={inputClass("first_name")}
              />
              {errors.first_name && (
                <p className="text-red-500 text-xs mt-1">{errors.first_name}</p>
              )}
            </div>
            <div>
              <label className="block text-foreground-700 text-xs font-medium mb-1.5">
                {t("plans.payment.lastName")}
              </label>
              <input
                type="text"
                value={form.last_name}
                onChange={(e) => handleChange("last_name", e.target.value)}
                placeholder={t("plans.payment.lastNamePlaceholder")}
                className={inputClass("last_name")}
              />
              {errors.last_name && (
                <p className="text-red-500 text-xs mt-1">{errors.last_name}</p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-foreground-700 text-xs font-medium mb-1.5">
              {t("plans.payment.email")}
            </label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => handleChange("email", e.target.value)}
              placeholder={t("plans.payment.emailPlaceholder")}
              className={inputClass("email")}
            />
            {errors.email && (
              <p className="text-red-500 text-xs mt-1">{errors.email}</p>
            )}
          </div>

          <div>
            <label className="block text-foreground-700 text-xs font-medium mb-1.5">
              {t("plans.payment.phone")}
            </label>
            <div className="flex gap-2">
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowCountries(!showCountries)}
                  className={`h-full flex items-center gap-1.5 px-3 rounded-xl border text-sm font-medium cursor-pointer whitespace-nowrap transition-colors ${
                    showCountries
                      ? "border-primary-400 bg-background-50 text-foreground-950"
                      : "border-background-200 bg-background-50 text-foreground-950 hover:bg-background-100"
                  }`}
                >
                  <span className="text-base leading-none">{selectedCountry.flag}</span>
                  <span>{selectedCountry.code}</span>
                  <i className={`${showCountries ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-foreground-400 text-xs`}></i>
                </button>

                {showCountries && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setShowCountries(false)}></div>
                    <div className="absolute top-full left-0 mt-1 z-20 w-56 max-h-52 overflow-y-auto bg-background-50 border border-background-200 rounded-xl shadow-lg py-1">
                      {COUNTRIES.map((country) => (
                        <button
                          key={country.code}
                          type="button"
                          onClick={() => handleCountrySelect(country.code)}
                          className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm cursor-pointer transition-colors hover:bg-background-100 ${
                            country.code === form.country_code
                              ? "bg-primary-100 text-primary-700 font-medium"
                              : "text-foreground-800"
                          }`}
                        >
                          <span className="text-base leading-none">{country.flag}</span>
                          <span className="flex-1 text-left">{country.name}</span>
                          <span className="text-foreground-400 text-xs">{country.code}</span>
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>

              <div className="flex-1 relative">
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => handleChange("phone", e.target.value)}
                  placeholder={digitPlaceholder}
                  maxLength={selectedCountry.digits}
                  className={`${inputClass("phone")} pr-14`}
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-foreground-400 pointer-events-none whitespace-nowrap">
                  {remainingDigits > 0
                    ? t("plans.payment.digitsLeft", { count: remainingDigits })
                    : remainingDigits === 0 && form.phone.length > 0 ? "✓" : ""}
                </span>
              </div>
            </div>
            {errors.phone && (
              <p className="text-red-500 text-xs mt-1">{errors.phone}</p>
            )}
            {!errors.phone && form.phone.length > 0 && form.phone.length < selectedCountry.digits && (
              <p className="text-foreground-400 text-xs mt-1">
                {remainingDigits === 1
                  ? t("plans.payment.enterMoreDigits", { count: remainingDigits })
                  : t("plans.payment.enterMoreDigitsPlural", { count: remainingDigits })}
              </p>
            )}
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="flex-1 px-4 py-3 rounded-full border border-background-200 text-foreground-600 text-sm font-medium hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
            >
              {t("plans.payment.cancel")}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
            >
              {loading ? (
                <>
                  <i className="ri-loader-4-line animate-spin text-sm"></i>
                  {t("plans.payment.processing")}
                </>
              ) : (
                <>
                  {t("plans.payment.payButton", { price: displayPrice })}
                  <i className="ri-arrow-right-line text-sm"></i>
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      <style>{`
        @keyframes modal-in {
          from { opacity: 0; transform: translateY(30px) scale(0.96); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        .animate-in-modal { animation: modal-in 0.3s ease-out; }
      `}</style>
    </div>
  );
}